import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { apiClient } from '../services/api';
import { XMarkIcon, CodeBracketIcon, ClipboardIcon } from '@heroicons/react/24/outline';

// Utility function to convert Buffer objects to strings
const convertBufferToString = (value) => {
  if (typeof value === 'object' && value?.type === 'Buffer' && Array.isArray(value.data)) {
    return String.fromCharCode(...value.data);
  }
  return value;
};

const BatchInvoiceDetails = () => {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const [batch, setBatch] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [validationInProgress, setValidationInProgress] = useState(false);
  const [submissionInProgress, setSubmissionInProgress] = useState(false);
  const [showJSON, setShowJSON] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // New state for batch processing with user-defined delay
  const [showDelayModal, setShowDelayModal] = useState(false);
  const [delaySeconds, setDelaySeconds] = useState(60);
  const [batchProcessingType, setBatchProcessingType] = useState('validate'); // 'validate' or 'submit'
  const [currentProcessingIndex, setCurrentProcessingIndex] = useState(0);
  const [processedInvoices, setProcessedInvoices] = useState([]);
  
  // New state for FBR response modal
  const [showFBRResponseModal, setShowFBRResponseModal] = useState(false);
  const [selectedFBRResponse, setSelectedFBRResponse] = useState(null);
  const [formattedInvoiceData, setFormattedInvoiceData] = useState(null);

  const loadBatchDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/batch-invoices/${batchId}`);
      setBatch(response.data.batch);
      setInvoices(response.data.invoices || []);
    } catch (error) {
      console.error('Failed to load batch details:', error);
      toast.error('Failed to load batch details');
      navigate('/app/batch-invoices');
    } finally {
      setLoading(false);
    }
  }, [batchId, navigate]);

  useEffect(() => {
    loadBatchDetails();
  }, [loadBatchDetails]);

  // Show delay input modal for batch validation
  const validateAllInvoices = () => {
    setBatchProcessingType('validate');
    setShowDelayModal(true);
  };

  // Show delay input modal for batch submission
  const submitAllInvoices = () => {
    setBatchProcessingType('submit');
    setShowDelayModal(true);
  };

  // Start batch processing with user-defined delay
  const startBatchProcessing = async () => {
    if (delaySeconds < 1 || delaySeconds > 300) {
      toast.error('Delay must be between 1 and 300 seconds');
      return;
    }

    setShowDelayModal(false);
    setProcessing(true);
    setCurrentProcessingIndex(0);
    setProcessedInvoices([]);

    if (batchProcessingType === 'validate') {
      setValidationInProgress(true);
    } else {
      setSubmissionInProgress(true);
    }

    const invoicesToProcess = batchProcessingType === 'validate' 
      ? invoices.filter(inv => inv.status !== 'Validated' && inv.status !== 'Submitted')
      : invoices.filter(inv => inv.status === 'Validated');

    console.log(`🔍 Batch processing debug:`);
    console.log(`   - Total invoices: ${invoices.length}`);
    console.log(`   - Processing type: ${batchProcessingType}`);
    console.log(`   - Invoices to process: ${invoicesToProcess.length}`);
    console.log(`   - Invoice statuses:`, invoices.map(inv => ({ id: inv.id, ref: inv.invoice_ref_no, status: inv.status })));

    if (invoicesToProcess.length === 0) {
      toast.warning(`No invoices available for ${batchProcessingType === 'validate' ? 'validation' : 'submission'}`);
      setProcessing(false);
      setValidationInProgress(false);
      setSubmissionInProgress(false);
      return;
    }

    toast.success(`Starting batch ${batchProcessingType}! Processing ${invoicesToProcess.length} invoices with ${delaySeconds}s delay...`);

    let successCount = 0;
    let failureCount = 0;

    for (let i = 0; i < invoicesToProcess.length; i++) {
      const invoice = invoicesToProcess[i];
      setCurrentProcessingIndex(i + 1);

      try {
        console.log(`🔄 Processing invoice ${i + 1}/${invoicesToProcess.length}: ${invoice.invoice_ref_no}`);
        console.log(`📋 Invoice status: ${invoice.status}`);
        console.log(`🎯 Action: ${batchProcessingType}`);
        
        // ✅ FIXED: Get properly formatted invoice data from backend with scenario calculations
        console.log(`📤 Fetching formatted invoice data from backend...`);
        const invoiceDataResponse = await apiClient.get(`/batch-invoices/${batchId}/invoice/${invoice.id}/fbr-data`);
        
        if (!invoiceDataResponse.data.success) {
          throw new Error(`Failed to get invoice data: ${invoiceDataResponse.data.message}`);
        }
        
        const invoiceData = invoiceDataResponse.data.invoiceData;
        console.log(`📤 Generated invoice data with scenario calculations:`, invoiceData);

        // Call the same route used for individual validation/submission
        const response = await apiClient.post('/fbr-validate-submit/process', {
          invoiceData: invoiceData,
          action: batchProcessingType,
          invoiceId: invoice.id
        });

        console.log(`📡 Response received:`, response.data);

        if (response.data.result?.success) {
          successCount++;
          console.log(`✅ Invoice ${invoice.invoice_ref_no} ${batchProcessingType}d successfully`);
        } else {
          failureCount++;
          console.log(`❌ Invoice ${invoice.invoice_ref_no} ${batchProcessingType} failed`);
        }

        // Store the result for potential display
        setProcessedInvoices(prev => [...prev, {
          invoice: invoice,
          response: response.data,
          success: response.data.result?.success || false
        }]);

        // Refresh batch details to show updated status
        await loadBatchDetails();

        // Wait for user-defined delay before next invoice (except for the last one)
        if (i < invoicesToProcess.length - 1) {
          console.log(`⏳ Waiting ${delaySeconds} seconds before next invoice...`);
          await new Promise(resolve => setTimeout(resolve, delaySeconds * 1000));
        }

      } catch (error) {
        failureCount++;
        console.error(`❌ Failed to ${batchProcessingType} invoice ${invoice.invoice_ref_no}:`, error);
        console.error(`❌ Error details:`, error.response?.data || error.message);
        
        setProcessedInvoices(prev => [...prev, {
          invoice: invoice,
          response: { 
            error: error.message,
            details: error.response?.data,
            status: error.response?.status
          },
          success: false
        }]);
      }
    }

    // Final cleanup and results
    setProcessing(false);
    setValidationInProgress(false);
    setSubmissionInProgress(false);
    setCurrentProcessingIndex(0);

    // Show completion message
    const totalProcessed = successCount + failureCount;
    if (failureCount === 0) {
      toast.success(`Batch ${batchProcessingType} completed! All ${successCount} invoices processed successfully.`);
    } else if (successCount === 0) {
      toast.error(`Batch ${batchProcessingType} completed! All ${failureCount} invoices failed.`);
    } else {
      toast.success(`Batch ${batchProcessingType} completed! ${successCount} succeeded, ${failureCount} failed out of ${totalProcessed} processed.`);
    }

    // Final refresh to show all updated statuses
    await loadBatchDetails();
  };



  const validateSingleInvoice = async (invoiceId) => {
    try {
      setValidationInProgress(true);

      // Find the invoice to validate
      const invoice = invoices.find(inv => inv.id === invoiceId);
      if (!invoice) {
        throw new Error('Invoice not found');
      }

      // ✅ FIXED: Get properly formatted invoice data from backend with scenario calculations
      console.log(`📤 Fetching formatted invoice data from backend...`);
      const invoiceDataResponse = await apiClient.get(`/batch-invoices/${batchId}/invoice/${invoiceId}/fbr-data`);
      
      if (!invoiceDataResponse.data.success) {
        throw new Error(`Failed to get invoice data: ${invoiceDataResponse.data.message}`);
      }
      
      const invoiceData = invoiceDataResponse.data.invoiceData;
      console.log('🚀 Validating invoice with FBR (with scenario calculations):', invoiceData);

      // Call our new FBR validation route
      const response = await apiClient.post('/fbr-validate-submit/process', {
        invoiceData: invoiceData,
        action: 'validate',
        invoiceId: invoiceId  // Pass invoice ID for status update
      });

      console.log('✅ FBR Validation Response:', response.data);

      setValidationResult(response.data);
      setShowValidationModal(true);

      if (response.data.result?.success) {
        const statusMessage = response.data.statusUpdated
          ? 'Invoice validated successfully with FBR! Status updated to Validated.'
          : 'Invoice validated successfully with FBR!';
        toast.success(statusMessage);
      } else {
        // FBR validation failed, but status should still be updated to "Validation Failed"
        const statusMessage = response.data.statusUpdated
          ? 'FBR validation failed. Status updated to Validation Failed.'
          : 'FBR validation failed. Check the details.';
        toast.error(statusMessage);
      }

      await loadBatchDetails(); // Refresh to show updated status
      setValidationInProgress(false);
    } catch (error) {
      console.error('❌ FBR Validation failed:', error);
      const errorData = error.response?.data;
      setValidationResult({
        success: false,
        error: errorData?.message || 'FBR validation failed',
        details: errorData
      });
      setShowValidationModal(true);
      setValidationInProgress(false);
    }
  };

  const submitSingleInvoice = async (invoiceId) => {
    try {
      setSubmissionInProgress(true);

      // Find the invoice to submit
      const invoice = invoices.find(inv => inv.id === invoiceId);
      if (!invoice) {
        throw new Error('Invoice not found');
      }

      // ✅ FIXED: Get properly formatted invoice data from backend with scenario calculations
      console.log(`📤 Fetching formatted invoice data from backend...`);
      const invoiceDataResponse = await apiClient.get(`/batch-invoices/${batchId}/invoice/${invoiceId}/fbr-data`);
      
      if (!invoiceDataResponse.data.success) {
        throw new Error(`Failed to get invoice data: ${invoiceDataResponse.data.message}`);
      }
      
      const invoiceData = invoiceDataResponse.data.invoiceData;
      console.log('🚀 Submitting invoice to FBR (with scenario calculations):', invoiceData);

      // Call our new FBR submission route
      const response = await apiClient.post('/fbr-validate-submit/process', {
        invoiceData: invoiceData,
        action: 'submit',
        invoiceId: invoiceId  // Pass invoice ID for status update
      });

      console.log('✅ FBR Submission Response:', response.data);

      if (response.data.result?.success) {
        const statusMessage = response.data.statusUpdated
          ? 'Invoice submitted successfully to FBR! Status updated to Submitted.'
          : 'Invoice submitted successfully to FBR!';
        toast.success(statusMessage);

        // Show FBR invoice number if available
        if (response.data.result?.data?.invoiceNumber) {
          toast.success(`FBR Invoice Number: ${response.data.result.data.invoiceNumber}`);
        }
      } else {
        // FBR submission failed, but status should still be updated to "Submission Failed"
        const statusMessage = response.data.statusUpdated
          ? 'FBR submission failed. Status updated to Submission Failed.'
          : 'FBR submission failed. Check the details.';
        toast.error(statusMessage);
      }

      await loadBatchDetails(); // Refresh to show updated status
      setSubmissionInProgress(false);
    } catch (error) {
      console.error('❌ FBR Submission failed:', error);
      toast.error(error.response?.data?.message || 'FBR submission failed');
      setSubmissionInProgress(false);
    }
  };

  const openInvoiceDetails = async (invoice) => {
    setSelectedInvoice(invoice);
    setShowInvoiceModal(true);
    setShowJSON(false); // Reset JSON view when opening new invoice
    setFormattedInvoiceData(null); // Reset formatted data
    
    // ✅ PRIORITY: Always fetch the EXACT JSON structure from backend that gets sent to FBR
    try {
      console.log(`📤 Fetching exact FBR JSON structure for invoice ${invoice.id}...`);
      const invoiceDataResponse = await apiClient.get(`/batch-invoices/${batchId}/invoice/${invoice.id}/fbr-data`);
      
      if (invoiceDataResponse.data.success) {
        console.log('✅ Got exact FBR JSON from backend:', invoiceDataResponse.data.invoiceData);
        setFormattedInvoiceData(invoiceDataResponse.data.invoiceData);
      } else {
        console.warn('⚠️ Backend failed to generate FBR JSON:', invoiceDataResponse.data.message);
        setFormattedInvoiceData({
          error: 'Failed to generate FBR JSON structure',
          message: invoiceDataResponse.data.message,
          fallback: normalizedFBRJSON(invoice)
        });
      }
    } catch (error) {
      console.error('❌ Failed to fetch exact FBR JSON structure:', error);
      setFormattedInvoiceData({
        error: 'Network error - could not fetch exact FBR JSON',
        message: error.response?.data?.message || error.message,
        note: 'This fallback may not match the exact structure sent to FBR',
        fallback: normalizedFBRJSON(invoice)
      });
    }
  };

  // Show FBR response when status button is clicked
  const showFBRResponse = async (invoice) => {
    try {
      // Try to get the stored FBR response from processed invoices first
      const processedInvoice = processedInvoices.find(p => p.invoice.id === invoice.id);
      
      if (processedInvoice && processedInvoice.response) {
        setSelectedFBRResponse({
          invoice: invoice,
          response: processedInvoice.response,
          type: processedInvoice.success ? 'success' : 'error'
        });
        setShowFBRResponseModal(true);
        return;
      }

      // If no stored response, make a fresh call to get current status
      toast.loading('Fetching FBR response...');
      
      // ✅ FIXED: Get properly formatted invoice data from backend with scenario calculations
      const invoiceDataResponse = await apiClient.get(`/batch-invoices/${batchId}/invoice/${invoice.id}/fbr-data`);
      
      if (!invoiceDataResponse.data.success) {
        throw new Error(`Failed to get invoice data: ${invoiceDataResponse.data.message}`);
      }
      
      const invoiceData = invoiceDataResponse.data.invoiceData;
      const action = invoice.status === 'Submitted' ? 'submit' : 'validate';
      
      const response = await apiClient.post('/fbr-validate-submit/process', {
        invoiceData: invoiceData,
        action: action,
        invoiceId: invoice.id
      });

      setSelectedFBRResponse({
        invoice: invoice,
        response: response.data,
        type: response.data.result?.success ? 'success' : 'error'
      });
      setShowFBRResponseModal(true);

    } catch (error) {
      console.error('Failed to get FBR response:', error);
      setSelectedFBRResponse({
        invoice: invoice,
        response: { error: error.message },
        type: 'error'
      });
      setShowFBRResponseModal(true);
    }
  };

  // Helper function to format NTN/CNIC - preserve original Excel format
  const formatNTNCNIC = (value) => {
    if (!value) return '';

    console.log('🔍 formatNTNCNIC input:', value, 'type:', typeof value);

    // Handle Buffer objects first
    const convertedValue = convertBufferToString(value);
    console.log('🔍 After convertBufferToString:', convertedValue, 'type:', typeof convertedValue);
    
    // Convert to string and handle scientific notation
    let str = String(convertedValue);
    if (str.includes('E') || str.includes('e')) {
      const num = parseFloat(str);
      str = num.toString(); // Use toString() instead of toFixed(0) to preserve original length
    }

    // Remove any non-numeric characters (symbols, dashes, spaces) but keep original length
    const numericOnly = str.replace(/[^0-9]/g, '');

    console.log('🔍 Final formatted NTN/CNIC:', numericOnly);
    // Return as-is (could be 7, 9, or 13 characters - whatever user provided from Excel)
    return numericOnly;
  };

  // Helper function to determine correct buyer registration type
  const getBuyerRegistrationType = (buyerNTNCNIC) => {
    if (!buyerNTNCNIC) return 'Unregistered';

    // Remove any formatting from NTN/CNIC
    const cleanNTN = String(buyerNTNCNIC).replace(/[^0-9]/g, '');

    // Common patterns for determining registration type:
    // - 13 digits usually indicates CNIC (individual) = Unregistered
    // - 7-9 digits usually indicates NTN (business) = Registered
    // - But this can vary, so we'll use a more conservative approach

    if (cleanNTN.length === 13) {
      // 13 digits = CNIC = Usually Unregistered (individual)
      return 'Unregistered';
    } else if (cleanNTN.length >= 7 && cleanNTN.length <= 9) {
      // 7-9 digits = NTN = Usually Registered (business)
      return 'Registered';
    } else {
      // Default to Unregistered for safety
      return 'Unregistered';
    }
  };

  // Normalize invoice data to FBR JSON format - following fbr_validate_test.js structure exactly
  const normalizedFBRJSON = (invoice) => {
    if (!invoice) return {};

    // ⚠️ FALLBACK ONLY: This should rarely be used since we fetch from backend
    // This is a simplified fallback that may not match exact backend calculations
    const jd = invoice.json_data || {};
    const normalized = {
      scenarioId: jd.scenarioId || invoice.scenario_id || 'SN001',
      invoiceType: jd.invoiceType || invoice.invoice_type || 'Sale Invoice',
      invoiceDate: (jd.invoiceDate || invoice.invoice_date || '').split('T')[0],
      sellerNTNCNIC: formatNTNCNIC(jd.sellerNTNCNIC || invoice.seller_ntn_cnic),
      sellerBusinessName: jd.sellerBusinessName || invoice.seller_business_name,
      sellerProvince: jd.sellerProvince || invoice.seller_province,
      sellerAddress: jd.sellerAddress || invoice.seller_address,
      buyerNTNCNIC: formatNTNCNIC(jd.buyerNTNCNIC || invoice.buyer_ntn_cnic),
      buyerBusinessName: jd.buyerBusinessName || invoice.buyer_business_name,
      buyerProvince: jd.buyerProvince || invoice.buyer_province,
      buyerAddress: jd.buyerAddress || invoice.buyer_address,
      buyerRegistrationType: jd.buyerRegistrationType || invoice.buyer_registration_type || getBuyerRegistrationType(formatNTNCNIC(jd.buyerNTNCNIC || invoice.buyer_ntn_cnic)),
      invoiceRefNo: jd.invoiceRefNo || invoice.invoice_ref_no || '',
      items: Array.isArray(jd.items) && jd.items.length > 0 ? jd.items : (invoice.items || []).map(it => {
        // ⚠️ SIMPLIFIED FALLBACK - May not match exact backend calculations
        let productDescription = it.product_description;
        if (!productDescription || productDescription === 'Product from Excel import' || productDescription === 'Test Product') {
          productDescription = 'Live horses, asses, mules and hinnies';
        }
        
        return {
          hsCode: it.hs_code || '0101.2100',
          productDescription: productDescription,
          rate: '18%', // Simplified - backend uses scenario-specific rates
          uoM: it.uom || 'PCS',
          quantity: parseFloat(it.quantity) || 1,
          totalValues: parseFloat(it.total_values) || 11.8,
          valueSalesExcludingST: parseFloat(it.value_sales_excluding_st) || 10,
          fixedNotifiedValueOrRetailPrice: parseFloat(it.fixed_notified_value_or_retail_price) || 0,
          salesTaxApplicable: parseFloat(it.sales_tax_applicable) || 1.8,
          salesTaxWithheldAtSource: parseFloat(it.sales_tax_withheld_at_source) || 0,
          extraTax: parseFloat(it.extra_tax) || 0,
          furtherTax: parseFloat(it.further_tax) || 0,
          sroScheduleNo: it.sro_schedule_no || '',
          fedPayable: parseFloat(it.fed_payable) || 0,
          discount: parseFloat(it.discount) || 0,
          saleType: 'Goods at standard rate (default)', // Simplified - backend uses scenario-specific types
          sroItemSerialNo: it.sro_item_serial_no || '',
          unitPriceExcludingTax: parseFloat(it.rate) || (parseFloat(it.value_sales_excluding_st) / parseFloat(it.quantity)) || 10,
          taxCalculationType: 'exclusive'
        };
      })
    };
    return normalized;
  };

  // Copy JSON to clipboard
  const copyJSON = async () => {
    try {
      // Use already fetched formatted data or fallback to local function
      let invoiceData;
      
      if (formattedInvoiceData) {
        // If there's an error, copy the fallback data
        invoiceData = formattedInvoiceData.error ? formattedInvoiceData.fallback : formattedInvoiceData;
      } else {
        // Last resort fallback
        invoiceData = normalizedFBRJSON(selectedInvoice);
      }
      
      await navigator.clipboard.writeText(JSON.stringify(invoiceData, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      
      // Show appropriate message
      if (formattedInvoiceData?.error) {
        toast.warning('Copied fallback JSON - may not match exact FBR structure');
      } else {
        toast.success('Copied exact FBR JSON structure');
      }
    } catch (error) {
      console.error('Failed to copy JSON:', error);
      toast.error('Failed to copy JSON to clipboard');
    }
  };

  const getStatusBadge = (status, invoice = null) => {
    const statusClasses = {
      'Draft': 'bg-gray-100 text-gray-800',
      'Validated': 'bg-green-100 text-green-800 hover:bg-green-200 cursor-pointer',
      'Validation Failed': 'bg-red-100 text-red-800 hover:bg-red-200 cursor-pointer',
      'Submitted': 'bg-blue-100 text-blue-800 hover:bg-blue-200 cursor-pointer',
      'Submission Failed': 'bg-red-100 text-red-800 hover:bg-red-200 cursor-pointer',
      'uploaded': 'bg-gray-100 text-gray-800',
      'validated': 'bg-green-100 text-green-800'
    };

    const isClickable = ['Validated', 'Validation Failed', 'Submitted', 'Submission Failed'].includes(status);

    if (isClickable && invoice) {
      return (
        <button
          onClick={() => showFBRResponse(invoice)}
          className={`px-2 py-1 text-xs font-medium rounded-full transition-colors ${statusClasses[status] || 'bg-gray-100 text-gray-800'}`}
          title="Click to view FBR response"
        >
          {status}
        </button>
      );
    }

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusClasses[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!batch) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Batch Not Found</h2>
          <button
            onClick={() => navigate('/app/batch-invoices')}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            Back to Batches
          </button>
        </div>
      </div>
    );
  }

  const totalInvoices = invoices.length;
  const validatedOk = invoices.filter(inv => inv.status === 'Validated' || inv.status === 'validated').length;
  const validationFail = invoices.filter(inv => inv.status === 'Validation Failed').length;
  const submittedOk = invoices.filter(inv => inv.status === 'Submitted').length;
  const submissionFail = invoices.filter(inv => inv.status === 'Submission Failed').length;

  const totalExcl = invoices.reduce((sum, inv) => sum + (parseFloat(inv.total_excl_tax) || 0), 0);
  const totalGst = invoices.reduce((sum, inv) => sum + (parseFloat(inv.total_gst) || 0), 0);
  const totalFurtherTax = invoices.reduce((sum, inv) => sum + (parseFloat(inv.total_further_tax) || 0), 0);
  const totalAmount = invoices.reduce((sum, inv) => sum + (parseFloat(inv.total_amount) || 0), 0);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Uploaded File Details - Batch #{batch.id}
              </h1>
              <p className="mt-2 text-gray-600">
                Process and manage invoices in this batch
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => window.open(`/api/batch-invoices/${batchId}/download-excel`, '_blank')}
                className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-50 transition-colors"
              >
                Open Excel/CSV Sheet
              </button>
              <button
                onClick={() => navigate('/app/batch-invoices')}
                className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-50 transition-colors"
              >
                ← Back to listings
              </button>
            </div>
          </div>
        </div>

        {/* Main content area */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column - Invoice stats and table */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Invoices Total in Uploaded File/Sheet/Batch</h2>
              
              {/* Stats cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-gray-900 text-white p-4 rounded-lg flex flex-col items-center justify-center">
                  <div className="text-sm mb-1">Total Invoices Uploaded</div>
                  <div className="text-3xl font-bold">{totalInvoices}</div>
                </div>
                <div className="bg-yellow-500 text-white p-4 rounded-lg flex flex-col items-center justify-center">
                  <div className="text-sm mb-1">Validated OK</div>
                  <div className="text-3xl font-bold">{validatedOk}</div>
                </div>
                <div className="bg-red-500 text-white p-4 rounded-lg flex flex-col items-center justify-center">
                  <div className="text-sm mb-1">Validation Fail</div>
                  <div className="text-3xl font-bold">{validationFail}</div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-green-600 text-white p-4 rounded-lg flex flex-col items-center justify-center">
                  <div className="text-sm mb-1">Submitted OK</div>
                  <div className="text-3xl font-bold">{submittedOk}</div>
                </div>
                <div className="bg-red-600 text-white p-4 rounded-lg flex flex-col items-center justify-center">
                  <div className="text-sm mb-1">Submission Fail</div>
                  <div className="text-3xl font-bold">{submissionFail}</div>
                </div>
              </div>
              
              {/* Financial summary */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
                <div className="bg-gray-100 p-3 rounded-lg">
                  <div className="text-xs font-medium text-gray-500">Excl</div>
                  <div className="text-sm font-bold text-gray-900">{formatCurrency(totalExcl)}</div>
                </div>
                <div className="bg-gray-100 p-3 rounded-lg">
                  <div className="text-xs font-medium text-gray-500">GST</div>
                  <div className="text-sm font-bold text-gray-900">{formatCurrency(totalGst)}</div>
                </div>
                <div className="bg-gray-100 p-3 rounded-lg">
                  <div className="text-xs font-medium text-gray-500">Further Tax</div>
                  <div className="text-sm font-bold text-gray-900">{formatCurrency(totalFurtherTax)}</div>
                </div>
                <div className="bg-blue-600 text-white p-3 rounded-lg col-span-2">
                  <div className="text-xs font-medium">Total</div>
                  <div className="text-sm font-bold">{formatCurrency(totalAmount)}</div>
                </div>
              </div>
            </div>
            
            {/* Invoices table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">Invoices - Details</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="max-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Buyer</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">NTN</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Registration</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">State</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lines</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Excl</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">GST</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Further Tax</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {invoices.map((invoice, index) => (
                      <tr key={invoice.id} className="hover:bg-gray-50">
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{index + 1}</td>
                        <td className="px-3 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-blue-600 cursor-pointer" onClick={() => openInvoiceDetails(invoice)}>
                            {invoice.invoice_ref_no || `INV-${String(index + 1).padStart(4, '0')}`}
                          </div>
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(invoice.invoice_date).toLocaleDateString()}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                          {invoice.buyer_business_name}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatNTNCNIC(invoice.buyer_ntn_cnic) || '-'}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                          <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                            {invoice.buyer_registration_type || 'Unregistered'}
                          </span>
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                          {invoice.buyer_province}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap">
                          {getStatusBadge(invoice.status, invoice)}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                          {invoice.items?.length || 0}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCurrency(invoice.total_excl_tax)}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCurrency(invoice.total_gst)}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCurrency(invoice.total_further_tax)}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {formatCurrency(invoice.total_amount)}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm font-medium space-x-1">
                          {invoice.status === 'Draft' && (
                            <button
                              onClick={() => validateSingleInvoice(invoice.id)}
                              disabled={validationInProgress}
                              className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs hover:bg-blue-200 transition-colors"
                            >
                              Validate
                            </button>
                          )}
                          {invoice.status === 'Validated' && (
                            <button
                              onClick={() => submitSingleInvoice(invoice.id)}
                              disabled={submissionInProgress}
                              className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs hover:bg-green-200 transition-colors"
                            >
                              Submit
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          
          {/* Right column - File info and actions */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Uploaded File Information</h2>
              <div className="mb-4">
                <div className="text-sm font-medium text-gray-700">Name:</div>
                <div className="text-sm text-gray-900 mb-2">{batch.original_filename || 'invoice_template_25.xlsx'}</div>
                <div className="text-sm font-medium text-gray-700">Uploaded:</div>
                <div className="text-sm text-gray-900">{formatDate(batch.created_at)}</div>
              </div>
              
              <div className="space-y-3 mt-6">
                <button
                  onClick={validateAllInvoices}
                  disabled={processing || validationInProgress}
                  className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {validationInProgress ? 'Validating...' : 'Validate All (Batch Processing)'}
                </button>
                <button
                  onClick={submitAllInvoices}
                  disabled={processing || submissionInProgress || validatedOk === 0}
                  className="w-full bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submissionInProgress ? 'Submitting...' : 'Submit All (Batch Processing)'}
                </button>
                <button
                  onClick={() => window.open(`/api/batch-invoices/${batchId}/download-validation-report`, '_blank')}
                  className="w-full bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Download Validation Batch Report
                </button>
                <button
                  onClick={() => window.open(`/api/batch-invoices/${batchId}/download-submission-report`, '_blank')}
                  className="w-full bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Download Submission Batch Report
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Invoice Details Modal */}
      {showInvoiceModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2">
          <div className="bg-white rounded-lg shadow-xl max-w-[98vw] w-full max-h-[95vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <div className="flex items-center space-x-3">
                <h3 className="text-xl font-semibold text-gray-900">
                  Invoice Details
                </h3>
                <div className="text-sm text-gray-500">
                  Invoice #{selectedInvoice.invoice_ref_no || selectedInvoice.invoice_number || `INV-${selectedInvoice.id}`}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setShowJSON(s => !s)}
                  className="flex items-center px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                  title="View FBR JSON data for validation/submission"
                >
                  <CodeBracketIcon className="w-4 h-4 mr-2" />
                  {showJSON ? 'Hide FBR JSON' : 'View FBR JSON'}
                </button>
                <button
                  onClick={() => setShowInvoiceModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* JSON Display Section */}
            {showJSON && (
              <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                <div className="bg-gray-900 text-green-200 text-sm rounded-lg p-4">
                  <div className="flex justify-between items-center mb-3">
                    <div className="flex items-center space-x-2">
                      <CodeBracketIcon className="w-5 h-5 text-green-400" />
                      <span className="font-semibold text-white">
                        {formattedInvoiceData?.error ? 'FBR JSON (Error/Fallback)' : 'FBR Validation/Submission JSON'}
                      </span>
                      <span className="text-xs text-gray-400">
                        {formattedInvoiceData?.error 
                          ? '(Fallback data - may not match exact FBR structure)'
                          : '(This is the exact JSON data sent to POST /api/fbr-validate-submit/process → FBR API)'
                        }
                      </span>
                    </div>
                    <button
                      onClick={copyJSON}
                      className="flex items-center px-3 py-1 bg-gray-700 text-white rounded hover:bg-gray-600 transition-colors"
                    >
                      <ClipboardIcon className="w-4 h-4 mr-1" />
                      {copied ? 'Copied!' : 'Copy JSON'}
                    </button>
                  </div>
                  
                  {/* Error message if backend failed */}
                  {formattedInvoiceData?.error && (
                    <div className="mb-3 p-2 bg-red-900 border border-red-700 rounded text-red-200 text-xs">
                      <div className="font-semibold">⚠️ Could not fetch exact FBR JSON structure:</div>
                      <div>{formattedInvoiceData.message}</div>
                      {formattedInvoiceData.note && <div className="mt-1 text-red-300">{formattedInvoiceData.note}</div>}
                    </div>
                  )}
                  
                  <pre className="overflow-auto max-h-96 bg-gray-800 p-3 rounded text-xs leading-relaxed">
{formattedInvoiceData ? 
  JSON.stringify(formattedInvoiceData.error ? formattedInvoiceData.fallback : formattedInvoiceData, null, 2) 
  : 'Loading exact FBR JSON structure from backend...'}
                  </pre>
                </div>
              </div>
            )}

            <div className="p-6">
              <div className="grid grid-cols-2 gap-6 mb-6">
                <div>
                  <div className="text-sm mb-1">
                    <span className="font-medium">Invoice:</span>{' '}
                    <span className="font-bold">{selectedInvoice.invoice_ref_no || `INV-${selectedInvoice.id}`}</span>
                  </div>
                  <div className="text-sm mb-1">
                    <span className="font-medium">Date:</span>{' '}
                    <span>{new Date(selectedInvoice.invoice_date).toLocaleDateString()}</span>
                  </div>
                  <div className="text-sm mb-1">
                    <span className="font-medium">Buyer:</span>{' '}
                    <span>{selectedInvoice.buyer_business_name}</span>
                  </div>
                  <div className="text-sm mb-1">
                    <span className="font-medium">NTN:</span>{' '}
                    <span>{formatNTNCNIC(selectedInvoice.buyer_ntn_cnic) || '-'}</span>
                  </div>
                  <div className="text-sm mb-1">
                    <span className="font-medium">State:</span>{' '}
                    <span>{selectedInvoice.buyer_province}</span>
                  </div>
                  <div className="text-sm mb-1">
                    <span className="font-medium">Registration:</span>{' '}
                    <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                      {selectedInvoice.buyer_registration_type || 'Unregistered'}
                    </span>
                  </div>
                  <div className="text-sm mb-1">
                    <span className="font-medium">Status:</span>{' '}
                    <span>{getStatusBadge(selectedInvoice.status, selectedInvoice)}</span>
                  </div>
                  <div className="text-sm mb-1">
                    <span className="font-medium">Address:</span>{' '}
                    <span>{selectedInvoice.buyer_address || 'Not provided'}</span>
                  </div>
                </div>
                <div>
                  <div className="text-sm mb-1">
                    <span className="font-medium">Lines:</span>{' '}
                    <span className="font-bold">{selectedInvoice.items?.length || 0}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div className="text-sm">
                      <span className="font-medium">Excl:</span>{' '}
                      <span className="font-bold">{formatCurrency(selectedInvoice.total_excl_tax)}</span>
                    </div>
                    <div className="text-sm">
                      <span className="font-medium">GST:</span>{' '}
                      <span className="font-bold">{formatCurrency(selectedInvoice.total_gst)}</span>
                    </div>
                    <div className="text-sm">
                      <span className="font-medium">Further Tax:</span>{' '}
                      <span className="font-bold">{formatCurrency(selectedInvoice.total_further_tax)}</span>
                    </div>
                    <div className="text-sm">
                      <span className="font-medium">Total:</span>{' '}
                      <span className="font-bold text-blue-600">{formatCurrency(selectedInvoice.total_amount)}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        #
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        HS Code
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        UoM
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Sale Type
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Rate %
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Qty
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Unit Price
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Excl
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        GST
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Further Tax
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {selectedInvoice.items && selectedInvoice.items.map((item, index) => {
                      // Use rate field from database (unit price) or calculate from value excluding sales tax
                      const unitPrice = parseFloat(item.rate) ||
                        (item.quantity > 0 ? item.value_sales_excluding_st / item.quantity : 0);
                      
                      return (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                            {index + 1}
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                            {item.hs_code}
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                            {item.uom}
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                            {item.sale_type_text || (item.sale_type === 1 ? 'Goods at standard rate (default)' : `Type ${item.sale_type}`)}
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                            {item.rate_percentage || item.sales_tax_rate || '18%'}
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                            {item.quantity}
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatCurrency(unitPrice)}
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                            {item.product_description}
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatCurrency(item.value_sales_excluding_st)}
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatCurrency(item.sales_tax_applicable)}
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatCurrency(item.further_tax)}
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {formatCurrency(parseFloat(item.value_sales_excluding_st) + parseFloat(item.sales_tax_applicable) + parseFloat(item.further_tax || 0) + parseFloat(item.extra_tax || 0) + parseFloat(item.fed_payable || 0) - parseFloat(item.discount || 0))}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowInvoiceModal(false)}
                  className="bg-gray-200 text-gray-800 px-4 py-2 rounded text-sm hover:bg-gray-300 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Validation Result Modal */}
      {showValidationModal && validationResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-xl font-semibold text-gray-900">
                Validation Result
              </h3>
              <button
                onClick={() => setShowValidationModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6">
              {validationResult.success ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center mb-2">
                    <div className="flex-shrink-0">
                      <div className="h-6 w-6 rounded-full bg-green-600 flex items-center justify-center text-white text-xs font-bold">
                        OK
                      </div>
                    </div>
                    <div className="ml-2">
                      <h3 className="text-base font-medium text-green-800">Validation Successful</h3>
                    </div>
                  </div>
                  <div className="text-xs text-gray-600 mb-3">
                    Dated: {new Date().toLocaleString()} | Status: Valid | Code: 00
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Issues (invoiceStatuses):</h4>
                    <ul className="space-y-2">
                      {validationResult.invoiceStatuses?.map((status, index) => (
                        <li key={index} className="flex items-start">
                          <div className="flex-shrink-0 mt-0.5">
                            <div className="h-5 w-5 rounded-full bg-green-600 flex items-center justify-center text-white text-xs font-bold">
                              {index + 1}
                            </div>
                          </div>
                          <div className="ml-2 text-sm text-gray-700">
                            {"{"}"itemSNo":"{status.itemSNo || index + 1}","statusCode":"00","status":"Valid","errorCode":"","error":""{"}"} 
                          </div>
                        </li>
                      )) || (
                        <li className="flex items-start">
                          <div className="flex-shrink-0 mt-0.5">
                            <div className="h-5 w-5 rounded-full bg-green-600 flex items-center justify-center text-white text-xs font-bold">
                              1
                            </div>
                          </div>
                          <div className="ml-2 text-sm text-gray-700">
                            {"{"}"itemSNo":"1","statusCode":"00","status":"Valid","errorCode":"","error":""{"}"} 
                          </div>
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
              ) : (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center mb-2">
                    <div className="flex-shrink-0">
                      <div className="h-6 w-6 rounded-full bg-red-600 flex items-center justify-center text-white text-xs font-bold">
                        Invalid
                      </div>
                    </div>
                    <div className="ml-2">
                      <h3 className="text-base font-medium text-red-800">Validation Failed</h3>
                    </div>
                  </div>
                  <div className="text-xs text-gray-600 mb-3">
                    Dated: {new Date().toLocaleString()} | Status: Invalid | Code: 01
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Issues (invoiceStatuses):</h4>
                    <ul className="space-y-2">
                      {validationResult.invoiceStatuses?.map((status, index) => (
                        <li key={index} className="flex items-start">
                          <div className="flex-shrink-0 mt-0.5">
                            <div className="h-5 w-5 rounded-full bg-red-600 flex items-center justify-center text-white text-xs font-bold">
                              {index + 1}
                            </div>
                          </div>
                          <div className="ml-2">
                            <div className="text-sm text-red-700 bg-red-100 p-1 px-2 rounded">
                              {status.error || "Provided Registration type does not match with Buyer's profile. Please verify and provide valid Buyer's registration type"}
                            </div>
                          </div>
                        </li>
                      )) || (
                        <li className="flex items-start">
                          <div className="flex-shrink-0 mt-0.5">
                            <div className="h-5 w-5 rounded-full bg-red-600 flex items-center justify-center text-white text-xs font-bold">
                              1
                            </div>
                          </div>
                          <div className="ml-2">
                            <div className="text-sm text-red-700 bg-red-100 p-1 px-2 rounded">
                              Provided Registration type does not match with Buyer's profile. Please verify and provide valid Buyer's registration type
                            </div>
                          </div>
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
              )}

              <div className="mt-6 flex justify-end space-x-2">
                <button
                  onClick={() => window.open(`/api/invoice/${validationResult.invoiceId || 'payload'}/validation-json`, '_blank')}
                  className="bg-blue-100 text-blue-800 px-4 py-2 rounded-md hover:bg-blue-200 transition-colors"
                >
                  Open Payload JSON
                </button>
                <button
                  onClick={() => setShowValidationModal(false)}
                  className="bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delay Input Modal */}
      {showDelayModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Batch {batchProcessingType === 'validate' ? 'Validation' : 'Submission'} Settings
                </h3>
                <button
                  onClick={() => setShowDelayModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Delay between invoices (seconds)
                </label>
                <input
                  type="number"
                  min="1"
                  max="300"
                  value={delaySeconds}
                  onChange={(e) => setDelaySeconds(parseInt(e.target.value) || 60)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter delay in seconds (1-300)"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Recommended: 60 seconds to avoid FBR rate limiting
                </p>
              </div>

              <div className="mb-4 p-3 bg-blue-50 rounded-md">
                <p className="text-sm text-blue-800">
                  <strong>Processing Details:</strong>
                </p>
                <ul className="text-xs text-blue-700 mt-1 space-y-1">
                  {batchProcessingType === 'validate' ? (
                    <>
                      <li>• Will process invoices that are not yet validated</li>
                      <li>• Each invoice will be sent to FBR for validation</li>
                      <li>• Status will be updated based on FBR response</li>
                    </>
                  ) : (
                    <>
                      <li>• Will process invoices with "Validated" status</li>
                      <li>• Each invoice will be submitted to FBR</li>
                      <li>• FBR invoice numbers will be generated on success</li>
                    </>
                  )}
                  <li>• Estimated time: {Math.ceil((invoices.filter(inv => 
                    batchProcessingType === 'validate' 
                      ? (inv.status !== 'Validated' && inv.status !== 'Submitted')
                      : inv.status === 'Validated'
                  ).length * delaySeconds) / 60)} minutes</li>
                </ul>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDelayModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={startBatchProcessing}
                  disabled={delaySeconds < 1 || delaySeconds > 300}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-400"
                >
                  Start {batchProcessingType === 'validate' ? 'Validation' : 'Submission'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* FBR Response Modal */}
      {showFBRResponseModal && selectedFBRResponse && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-4/5 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    FBR Response Details
                  </h3>
                  <p className="text-sm text-gray-500">
                    Invoice: {selectedFBRResponse.invoice.invoice_ref_no} | 
                    Status: {selectedFBRResponse.invoice.status}
                  </p>
                </div>
                <button
                  onClick={() => setShowFBRResponseModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="max-h-96 overflow-y-auto">
                {selectedFBRResponse.type === 'success' ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center mb-2">
                      <div className="flex-shrink-0">
                        <div className="h-6 w-6 rounded-full bg-green-600 flex items-center justify-center text-white text-xs font-bold">
                          ✓
                        </div>
                      </div>
                      <div className="ml-2">
                        <h3 className="text-base font-medium text-green-800">
                          {selectedFBRResponse.response.result?.type === 'submission' ? 'Submission Successful' : 'Validation Successful'}
                        </h3>
                      </div>
                    </div>
                    
                    {selectedFBRResponse.response.result?.data?.invoiceNumber && (
                      <div className="mb-3 p-2 bg-green-100 rounded">
                        <p className="text-sm font-medium text-green-800">
                          FBR Invoice Number: {selectedFBRResponse.response.result.data.invoiceNumber}
                        </p>
                      </div>
                    )}

                    <div className="text-xs text-gray-600 mb-3">
                      Dated: {selectedFBRResponse.response.result?.data?.dated || new Date().toLocaleString()} | 
                      Status: {selectedFBRResponse.response.result?.data?.validationResponse?.status || 'Valid'} | 
                      Code: {selectedFBRResponse.response.result?.data?.validationResponse?.statusCode || '00'}
                    </div>

                    {selectedFBRResponse.response.result?.data?.validationResponse?.invoiceStatuses && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Item Validation Results:</h4>
                        <ul className="space-y-2">
                          {selectedFBRResponse.response.result.data.validationResponse.invoiceStatuses.map((status, index) => (
                            <li key={index} className="flex items-start">
                              <div className="flex-shrink-0 mt-0.5">
                                <div className="h-5 w-5 rounded-full bg-green-600 flex items-center justify-center text-white text-xs font-bold">
                                  {status.itemSNo || index + 1}
                                </div>
                              </div>
                              <div className="ml-2 text-sm text-gray-700">
                                Status: {status.status} | Code: {status.statusCode}
                                {status.invoiceNo && <span> | Invoice: {status.invoiceNo}</span>}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center mb-2">
                      <div className="flex-shrink-0">
                        <div className="h-6 w-6 rounded-full bg-red-600 flex items-center justify-center text-white text-xs font-bold">
                          ✗
                        </div>
                      </div>
                      <div className="ml-2">
                        <h3 className="text-base font-medium text-red-800">
                          {selectedFBRResponse.response.result?.type === 'submission' ? 'Submission Failed' : 'Validation Failed'}
                        </h3>
                      </div>
                    </div>
                    
                    <div className="text-xs text-gray-600 mb-3">
                      Error: {selectedFBRResponse.response.error || selectedFBRResponse.response.message || 'Unknown error'}
                    </div>

                    {selectedFBRResponse.response.result?.data?.validationResponse?.invoiceStatuses && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Issues Found:</h4>
                        <ul className="space-y-2">
                          {selectedFBRResponse.response.result.data.validationResponse.invoiceStatuses.map((status, index) => (
                            <li key={index} className="flex items-start">
                              <div className="flex-shrink-0 mt-0.5">
                                <div className="h-5 w-5 rounded-full bg-red-600 flex items-center justify-center text-white text-xs font-bold">
                                  {index + 1}
                                </div>
                              </div>
                              <div className="ml-2">
                                <div className="text-sm text-red-700 bg-red-100 p-1 px-2 rounded">
                                  {status.error || 'Validation failed'}
                                </div>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {/* Raw Response Data */}
                <div className="mt-4">
                  <details className="bg-gray-50 rounded-lg p-3">
                    <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
                      View Raw FBR Response
                    </summary>
                    <pre className="mt-2 text-xs bg-gray-800 text-green-200 p-3 rounded overflow-auto max-h-64">
                      {JSON.stringify(selectedFBRResponse.response, null, 2)}
                    </pre>
                  </details>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowFBRResponseModal(false)}
                  className="bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Processing Progress Indicator */}
      {processing && (
        <div className="fixed bottom-4 right-4 bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-40">
          <div className="flex items-center space-x-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <div>
              <p className="text-sm font-medium text-gray-900">
                Processing {batchProcessingType}...
              </p>
              {currentProcessingIndex > 0 && (
                <p className="text-xs text-gray-500">
                  Invoice {currentProcessingIndex} of {invoices.filter(inv => 
                    batchProcessingType === 'validate' 
                      ? (inv.status !== 'Validated' && inv.status !== 'Submitted')
                      : inv.status === 'Validated'
                  ).length}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchInvoiceDetails;
